<script>
import { mapGetters } from "vuex";
import { merge } from "lodash";
import { cleanTaxId } from "@/utils/validators";

import leave from "@/mixins/leave";

import patientService from "@/services/patient";
import companyService from "@/services/company";
import medicalInsuranceService from "@/services/medical-insurance";
import fileUploadConfigService from "@/services/fileupload-config";
import segmentService from "@/services/segment";
import {
  DOC_TYPES,
  DOC_TYPE_DEFAULT,
  TAX_ID_TYPES,
} from "@/services/constants";
import userService from "@/services/user";

import Spinner from "@/components/ui/Spinner";
import FileUpload from "@/components/ui/FileUpload";
import InputTag from "@/components/ui/InputTag";
import UserAddressSection from "@/components/user/UserAddressSection";
import { theme } from "@/services/config";
import { enableScores } from "@/services/config";
import resourceService from "@/services/resource";
import { capitalize } from "@/services/helper";

export default {
  name: "PatientDetail",
  mixins: [leave],

  components: {
    Spinner,
    FileUpload,
    InputTag,
    UserAddressSection,
  },

  data() {
    return {
      capitalize,
      theme,
      isLoading: false,
      countries: undefined,
      cities: undefined,
      useScores: enableScores,
      companies: [],
      medicalInsurances: [],

      patientId: this.$route.params.id,
      docTypes: DOC_TYPES,
      taxIdTypes: TAX_ID_TYPES,
      doctors: [],
      originalEmail: "",
      patient: {
        publicGroupId: "",
        publicId: "",
        doctor: null,
        firstName: "",
        lastName: "",
        bornAt: "",
        username: "",
        email: "",
        phone: "",
        secondaryPhone: "",
        gender: "m",
        governmentId: {
          type: DOC_TYPE_DEFAULT,
          number: "",
        },
        taxId: {
          type: "",
          number: "",
        },
        ...(theme === "dash" && {
          dashExtension: {
            nationalityCity: {
              name: "",
            },
          },
        }),
        address: {
          street: "",
          city: "",
          district: theme === "dash" ? undefined : "Buenos Aires",
          country: theme === "dash" ? undefined : "Argentina",
          postalCode: "",
          betweenStreets: ["", ""],
        },
        patient: {
          medicalInsuranceObj: {
            insurance: undefined,
            publicInsurance: {
              publicId: "",
              name: "",
            },
            number: "",
            plan: "",
          },
          health: {
            bloodType: "",
            height: "",
            weight: "",
            allergies: [],
            additionalInformation: "",
            diagnosis: "",
          },
        },
      },
      files: [],
      isUpdate: this.$route.name === "update-patient",
      isFetchingPatient: false,
      isPostingPatient: false,
      hasFiles: false,
      hasPendingFileUpload: false, // Bandera para saber si hay archivo pendiente de subir
      disableMedicalInsurance: theme !== "",
      originalDoctor: "",
      isLoadingMedicalInsurances: false,
      usesExternalMedicalInsurance: true,
    };
  },

  computed: {
    ...mapGetters(["isAdmin", "isDoctor"]),
    uploadMediaEndpoint() {
      return fileUploadConfigService.getUploadAvatarEndpoint(
        "users",
        this.patientId
      );
    },

    sectors() {
      if (!this.patient.company) {
        return [];
      }

      const company = this.companies.find(
        (c) => c._id === this.patient.company
      );

      return company?.sectors;
    },

    canChangeEmail() {
      if (!this.isUpdate) {
        return true;
      }

      return !this.isDoctor;
    },

    hasEmailChanged() {
      if (!this.isUpdate) {
        return false;
      }

      return this.patient.email !== this.originalEmail;
    },

    showDeviceSection() {
      return this.isAdmin && this.isUpdate && this.patient.device;
    },

    medicalInsurancePropValue: {
      get() {
        return this.usesExternalMedicalInsurance
          ? this.patient.patient.medicalInsuranceObj.publicInsurance.name
          : this.patient.patient.medicalInsuranceObj.insurance;
      },
      set(value) {
        if (this.usesExternalMedicalInsurance) {
          // Buscar el seguro médico por ID y obtener su nombre
          const selectedInsurance = this.medicalInsurances.find(
            (insurance) => insurance._id === value
          );
          if (selectedInsurance) {
            this.patient.patient.medicalInsuranceObj.publicInsurance.name =
              selectedInsurance.name;
            this.patient.patient.medicalInsuranceObj.publicInsurance.publicId =
              selectedInsurance._id;
          }
        } else {
          this.patient.patient.medicalInsuranceObj.insurance = value;
        }
      },
    },

    governmentIdType: {
      get() {
        if (!this.patient.governmentId) {
          return DOC_TYPE_DEFAULT;
        }
        return this.patient.governmentId.type || DOC_TYPE_DEFAULT;
      },
      set(value) {
        if (!this.patient.governmentId) {
          this.$set(this.patient, "governmentId", {
            type: value,
            number: "",
          });
        } else {
          this.$set(this.patient.governmentId, "type", value);
        }
      },
    },
  },

  created() {
    this.getPatient();
    this.getDoctors();
    this.getCountries();
    this.getCities();
    this.getMedicalInsurances();

    if (this.useScores) {
      this.$set(this.patient, "company", null);
      this.$set(this.patient, "companySector", null);
      this.getCompanies();
    }
  },

  mounted() {
    document.addEventListener("keyup", this.escape);

    // Limpiar el FileUpload cuando se monta en modo creación
    if (!this.isUpdate) {
      this.$nextTick(() => {
        const dropzone = this.$refs.fileUpload?.$refs?.uploader?.dropzone;
        if (dropzone) {
          dropzone.removeAllFiles(true);
        }
        // Limpiar también previewFiles
        if (this.$refs.fileUpload) {
          this.$refs.fileUpload.previewFiles = [];
        }
      });
    }
  },

  beforeDestroy() {
    document.removeEventListener("keyup", this.escape);
  },

  methods: {
    escape(event) {
      if (event.keyCode == 27) this.goToRoute("patient");
    },

    submit() {
      this.createOrUpdatePatient();
    },

    createOrUpdatePatient() {
      const eventName = this.isUpdate ? "Patient Updated" : "Patient Created";
      segmentService.track({ name: eventName });

      this.isPostingPatient = true;
      this.showLeaveGuard = false;
      const serviceCall = this.isUpdate
        ? patientService.update
        : patientService.create;

      // Auto-generar username en creación: DNI siempre que haya DNI; el email
      // solo como último recurso. Consistente con el backend.
      if (!this.isUpdate) {
        if (this.patient.governmentId?.number) {
          this.patient.username = this.patient.governmentId.number.trim();
        } else if (this.patient.email) {
          this.patient.username = this.patient.email.toLowerCase().trim();
        }
      }

      const patientPayload = { ...this.patient };
      cleanTaxId(patientPayload);

      // Si es un doctor creando un paciente y no hay doctor asignado, asignarse a sí mismo
      if (this.isDoctor && !this.isUpdate && !patientPayload.doctor) {
        patientPayload.doctor = this.$store.state.user._id;
      }

      if (
        !patientPayload.doctor ||
        (typeof patientPayload.doctor === "object" &&
          Object.keys(patientPayload.doctor).length === 0)
      ) {
        delete patientPayload.doctor;
      }

      const promises = [
        serviceCall({
          ...patientPayload,
          publicId: undefined,
          avatar: undefined, // No enviar avatar en el payload, se sube por separado
        }),
      ];

      return Promise.all(promises)
        .then(([patient]) => {
          this.patientId = this.patientId || patient._id;
          const patientUpdate = patient || this.patient;
          patientUpdate.doctors = [
            this.doctors.find(({ _id }) => this.patient.doctor === _id),
          ];

          // Find-or-associate: el backend devuelve `existed: true` cuando ya
          // había un paciente con ese DNI. No es un error: se lo asociamos al
          // médico que lo da de alta. Informamos al usuario en vez de crear
          // un duplicado.
          if (patient && patient.existed) {
            this.$message({
              type: "success",
              message:
                "Este paciente ya existía en la plataforma. Te lo asociamos como paciente tuyo también.",
              duration: 6000,
            });
          }

          this.$bus.$emit("patient-update", patientUpdate);

          // FG: It is stored in localStorage so that Prescription.vue can select it automatically
          localStorage.setItem(
            "newlyCreatedPatient",
            JSON.stringify(patientUpdate)
          );

          if (this.hasFiles || this.hasPendingFileUpload) {
            this.$nextTick(() => {
              if (this.isUpdate) {
                // En modo edición, subir inmediatamente (no hay problemas de permisos)
                this.handleUploadFiles();
              } else {
                // En modo creación, agregar delay para que el backend procese los permisos
                // Usar 1 segundo para doctores, 500ms para admins
                const delay = this.isDoctor ? 1000 : 500;
                setTimeout(() => {
                  this.handleUploadFiles();
                }, delay);
              }
            });
          } else {
            this.goToRoute("patient");
          }
        })
        .catch(() => {
          this.isPostingPatient = false;
        });
    },

    getPatient() {
      if (this.isUpdate) {
        this.isLoading = true;
        this.isFetchingPatient = true;
        patientService
          .getById(this.patientId)
          .then((patient) => {
            patient.doctor = patient.doctor ? patient.doctor._id : null;
            patient.address.betweenStreets =
              patient.address.betweenStreets || [];

            // Asegurar que los objetos anidados existen
            if (!patient.governmentId) {
              patient.governmentId = {
                type: DOC_TYPE_DEFAULT,
                number: "",
              };
            }
            if (!patient.taxId) {
              patient.taxId = {
                type: "",
                number: "",
              };
            }

            this.originalEmail = patient.email;
            this.patient = merge({}, this.patient, {
              ...patient,
              doctors: patient.doctors.map((elem) => elem._id),
            });
            this.originalDoctor = patient.doctor;
          })
          .finally(() => {
            this.isFetchingPatient = false;
            this.isLoading = false;
          });
      }
    },

    getDoctors() {
      userService.getDoctors().then(({ docs }) => (this.doctors = docs));
    },

    getCompanies() {
      companyService.get().then(({ docs }) => (this.companies = docs));
    },

    getMedicalInsurances(query) {
      this.isLoadingMedicalInsurances = true;
      medicalInsuranceService
        .get({ name: { $regex: query, $options: "i" } })
        .then(({ docs }) => (this.medicalInsurances = docs))
        .finally(() => (this.isLoadingMedicalInsurances = false));
    },

    goToRoute(name) {
      if (name === "patient") {
        this.$router.back();
      } else {
        this.$router.push({ name });
      }
    },

    getModalName() {
      return this.isUpdate ? "Actualizar Paciente" : "Nuevo Paciente";
    },

    handleUploadFiles() {
      this.$refs.fileUpload && this.$refs.fileUpload.startUpload();
    },

    getFiles() {
      if (!this.patient.avatar || !this.patient.avatar.key) {
        return [];
      }

      const avatar = this.patient.avatar;
      const fileName =
        avatar.originalName || avatar.key?.split("/").pop() || "avatar.jpeg";

      return [
        {
          key: avatar.key,
          size: avatar.size,
          mimeType: avatar.mimeType,
          originalName: fileName,
        },
      ];
    },

    getDropdownOptions() {
      return {
        method: "put",
        paramName: "avatar",
        maxFiles: 1,
      };
    },

    uploadFilesSuccess() {
      this.hasPendingFileUpload = false; // Limpiar bandera después de subir
      this.goToRoute("patient");
    },

    uploadFilesError() {
      this.hasPendingFileUpload = false; // Limpiar bandera en caso de error
      this.isPostingPatient = false;
    },

    onFileAdded() {
      this.hasFiles = true;
      this.hasPendingFileUpload = true;

      // En modo edición, eliminar archivos previos del servidor cuando se agrega uno nuevo
      if (this.isUpdate) {
        const fileUpload = this.$refs.fileUpload;
        const dropzone = fileUpload?.$refs?.uploader?.dropzone;
        if (dropzone) {
          // Eliminar archivos del servidor (con key) para que solo quede el nuevo
          const serverFiles = dropzone.files.filter((f) => f.key);
          serverFiles.forEach((file) => {
            // Eliminar del array de archivos sin emitir eventos
            const fileIndex = dropzone.files.indexOf(file);
            if (fileIndex > -1) {
              dropzone.files.splice(fileIndex, 1);
            }
            // Eliminar de previewFiles también
            if (fileUpload.previewFiles) {
              const previewIndex = fileUpload.previewFiles.findIndex(
                (f) => f.key === file.key
              );
              if (previewIndex > -1) {
                fileUpload.previewFiles.splice(previewIndex, 1);
              }
            }
            // Eliminar el preview del DOM
            if (file.previewElement && file.previewElement.parentNode) {
              file.previewElement.parentNode.removeChild(file.previewElement);
            }
          });
        }
      }
    },

    onFileRemoved() {
      // Verificar si realmente no hay archivos en el dropzone
      const dropzone = this.$refs.fileUpload?.$refs?.uploader?.dropzone;
      const filesCount = dropzone?.files?.length || 0;
      const userFiles = dropzone?.files?.filter((f) => !f.key) || [];

      // Solo marcar hasFiles = false si NO hay archivos de usuario
      if (filesCount === 0 || (userFiles.length === 0 && filesCount === 0)) {
        this.hasFiles = false;
        this.patient.avatar = null;
      }
    },

    async getCountries() {
      this.isLoading = true;
      try {
        const countries = await resourceService.getCountries();
        this.countries = countries;
      } finally {
        this.isLoading = false;
      }
    },

    async getCities() {
      this.isLoading = true;
      try {
        const cities = await resourceService.getCities();
        this.cities = cities;
      } finally {
        this.isLoading = false;
      }
    },

    generateUsername() {
      // El username del paciente es su DNI (consistente con el backend).
      if (!this.isUpdate && this.patient.governmentId?.number) {
        this.patient.username = this.patient.governmentId.number.trim();
      }
    },
  },

  watch: {
    "patient.patient.medicalInsurance"() {
      if (!this.patient.patient.medicalInsurance) {
        delete this.patient.patient.medicalInsurance;
      }
    },
    "patient.governmentId.type"() {
      // Asegurar que el objeto governmentId existe
      if (!this.patient.governmentId) {
        this.$set(this.patient, "governmentId", {
          type: DOC_TYPE_DEFAULT,
          number: "",
        });
      }
    },
    "patient.taxId.type"() {
      // Asegurar que el objeto taxId existe
      if (!this.patient.taxId) {
        this.$set(this.patient, "taxId", {
          type: "",
          number: "",
        });
      }
    },
    "patient.governmentId.number"() {
      this.generateUsername();
    },
    "patient.firstName"() {
      this.generateUsername();
    },
    "patient.lastName"() {
      this.generateUsername();
    },
  },
};
</script>

<template lang="pug">
ValidationObserver(v-slot="{handleSubmit}")
  form.patient-form(@submit.prevent="handleSubmit(submit)")
    //- Header
    .patient-form__header
      .patient-form__header-left
        .patient-form__back(@click="goToRoute('patient')")
          micon(name="arrow_back")
        h2.patient-form__title {{ isUpdate ? 'Editar paciente' : 'Agregar paciente' }}
      .patient-form__header-right
        el-button(@click="goToRoute('patient')") Cancelar
        el-button(type="primary" native-type="submit" :loading="isPostingPatient") Guardar
    p.patient-form__subtitle Los campos con (*) son obligatorios

    spinner(v-if="isFetchingPatient")
    template(v-else)
      //- 1. Información Personal
      .form-section
        .form-section__title Información Personal
        .form-row.form-row--4col
          .form-field
            label.form-field__label
              span.required *
              | Nombre
            ValidationProvider(name="nombre" rules="required" v-slot="{ errors }")
              el-input(v-model="patient.firstName")
              span.has-error {{ errors[0] }}
          .form-field
            label.form-field__label
              span.required *
              | Apellido
            ValidationProvider(name="apellido" rules="required" v-slot="{ errors }")
              el-input(v-model="patient.lastName")
              span.has-error {{ errors[0] }}
          .form-field
            label.form-field__label
              span.required *
              | Tipo de Documento
            el-select(v-model="governmentIdType" :disabled="isUpdate" placeholder="" default-first-option filterable)
              el-option(v-for="docType in docTypes" :key="docType" :value="docType" :label="docType.toUpperCase()")
          .form-field
            label.form-field__label
              span.required *
              | Número de Documento
            ValidationProvider(name="número de documento" rules="required" v-slot="{ errors }")
              el-input(v-model="patient.governmentId.number")
              span.has-error {{ errors[0] }}
        .form-row.form-row--4col
          .form-field
            label.form-field__label Tipo de Identificación Fiscal
            el-select(v-model="patient.taxId.type" placeholder="" default-first-option filterable)
              el-option(v-for="taxType in taxIdTypes" :key="taxType" :value="taxType" :label="taxType.toUpperCase()")
          .form-field
            label.form-field__label Nº Identificación Fiscal
            el-input(v-model="patient.taxId.number" type="number" placeholder="Sin guiones ni caracteres especiales")
          .form-field
            label.form-field__label
              span.required(v-if="!isUpdate") *
              | Fecha de Nacimiento
            ValidationProvider(name="fecha de nacimiento" :rules="isUpdate ? 'priorToday' : 'required|priorToday'" v-slot="{ errors }")
              el-date-picker(
                v-model="patient.bornAt"
                placeholder="dd/mm/aaaa"
                format="dd/MM/yyyy"
                lang="es"
                name="patient-birth-date"
                autocomplete="off"
                style="width: 100%"
              )
              span.has-error {{ errors[0] }}
          .form-field
            label.form-field__label
              span.required *
              | Género
            .form-field__radios
              el-radio(v-model="patient.gender" label="m" checked) Masculino
              el-radio(v-model="patient.gender" label="f") Femenino
        .form-row(v-if="theme === 'dash'")
          .form-field
            label.form-field__label Segundo Apellido
            el-input(v-model="patient.secondLastName")
          .form-field
            label.form-field__label
              span.required *
              | Lugar de Nacimiento
            ValidationProvider(name="nationalityCity" rules="required" v-slot="{ errors }")
              el-select(v-model="patient.dashExtension.nationalityCity.name" placeholder="" name="nationalityCity" filterable)
                el-option(
                  v-for="city in cities"
                  :key="city.value"
                  :label="capitalize(city.name)"
                  :value="city.name"
                )
              span.has-error {{ errors[0] }}
        .form-row
          .form-field(v-if="isUpdate")
            label.form-field__label Usuario
            el-input(v-model="patient.username" disabled)
          .form-field
            label.form-field__label(:class="{ 'required-label': patient.governmentId.type === 'pas' }") Nacionalidad
            ValidationProvider(name="nationality" :rules="patient.governmentId.type === 'pas' ? 'required' : ''" v-slot="{ errors }")
              el-select(v-model="patient.nationality" placeholder="" filterable clearable default-first-option :disabled="patient.governmentId.type !== 'pas'")
                el-option(
                  v-for="country in countries"
                  :key="country.value"
                  :label="capitalize(country.name)"
                  :value="country.name"
                )
              span.has-error {{ errors[0] }}

      //- 2. Cobertura
      .form-section
        .form-section__title Cobertura
        .form-row(v-if="isAdmin")
          .form-field
            label.form-field__label ID Proveedor
            el-input(v-model="patient.publicId" name="ID Público" disabled)
        .form-row
          .form-field
            label.form-field__label Seguro Médico
            el-select(
              v-model="medicalInsurancePropValue"
              clearable
              remote
              filterable
              placeholder="Ingrese un seguro médico"
              :remote-method="getMedicalInsurances"
              :loading="isLoadingMedicalInsurances"
            )
              el-option(
                v-for="medicalInsurance in medicalInsurances"
                :key="medicalInsurance._id"
                :label="medicalInsurance.name"
                :value="medicalInsurance._id"
              )
          .form-field
            label.form-field__label Número de afiliado
            el-input(v-model="patient.patient.medicalInsuranceObj.number" name="número de afiliado" autocomplete="off")
          .form-field
            label.form-field__label Plan
            el-input(v-model="patient.patient.medicalInsuranceObj.plan")

      //- 3. Contacto
      .form-section
        .form-section__title Contacto
        .form-row
          .form-field
            label.form-field__label
              span.required *
              | Email
            ValidationProvider(name="email" rules="required|email" v-slot="{ errors }")
              el-input(type="email" v-model="patient.email" :disabled="!canChangeEmail")
              span.has-error {{ errors[0] }}
              span.has-warning(v-show="hasEmailChanged") Al modificar el email, el usuario debera volver a validar la invitación
          .form-field
            label.form-field__label
              span.required(v-if="!isUpdate") *
              | Teléfono
            ValidationProvider(name="teléfono" :rules="isUpdate ? '' : 'required'" v-slot="{ errors }")
              el-input(v-model="patient.phone")
              span.has-error {{ errors[0] }}
          .form-field
            label.form-field__label Teléfono secundario
            el-input(v-model="patient.secondaryPhone")

      //- 4. Dirección
      .form-section(v-if="cities && cities.length")
        .form-section__title Dirección
        user-address-section(v-model="patient" :cities="cities")

      //- 5. Foto del paciente
      .form-section
        .form-section__title Foto del paciente
        .form-photo-upload
          file-upload(
            ref="fileUpload"
            type="image"
            :url="uploadMediaEndpoint"
            :files="isUpdate ? getFiles() : []"
            :dropzone-options="getDropdownOptions()"
            @file-added="onFileAdded"
            @files-removed="onFileRemoved"
            @fileupload-success="uploadFilesSuccess"
            @fileupload-error="uploadFilesError"
          )

      //- Footer buttons
      .patient-form__footer
        el-button(@click="goToRoute('patient')") Cancelar
        el-button(type="primary" native-type="submit" :loading="isPostingPatient") Guardar
</template>

<style lang="scss" scoped>
// Header
.patient-form__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.patient-form__header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.patient-form__back {
  cursor: pointer;
  display: flex;
  align-items: center;
  .micon {
    fill: $text-secondary-new;
    width: 22px;
    height: 22px;
  }
  &:hover .micon {
    fill: $navy;
  }
}

.patient-form__title {
  font-size: 20px;
  font-weight: 700;
  color: $navy;
  margin: 0;
}

.patient-form__subtitle {
  font-size: 13px;
  color: $text-secondary-new;
  margin-bottom: 24px;
}

// Sections
.form-section {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  background: white;
}

.form-section__title {
  font-size: 14px;
  font-weight: 700;
  color: $navy;
  margin-bottom: 16px;
}

// Grid rows
.form-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 12px;
}

.form-row--4col {
  grid-template-columns: repeat(4, 1fr);
}

// Photo upload section
.form-photo-upload {
  max-width: 300px;
}

// Fields
.form-field {
  min-width: 0;
}

.form-field__label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7294;
  margin-bottom: 4px;

  .required {
    color: #ef4444;
    margin-right: 2px;
  }
}

.required-label::after {
  content: " *";
  color: #ef4444;
}

.form-field__radios {
  display: flex;
  gap: 16px;
  padding-top: 6px;
}

// Footer
.patient-form__footer {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;

  .el-button {
    min-width: 200px;
    border-radius: 8px;
  }
}

// Overrides
.el-select,
.el-date-editor {
  width: 100%;
}

.has-error {
  color: #ef4444;
  font-size: 12px;
}

.has-warning {
  color: #f59e0b;
  font-size: 12px;
}
</style>
